import { colors } from '@/theme/foundations';
import {
  CasePriority,
  CaseScope,
  CaseSortField,
  CaseSortOrder,
  CaseStatus,
  CaseType,
  HumanCaseType,
} from '@/types/cases';

export const CASES_PAGE_SIZE = 20;
export const CASES_STALE_TIME = 30_000;
export const CASES_GC_TIME = 5 * 60 * 1000;

export const CASES_QUERY_OPTIONS = {
  staleTime: CASES_STALE_TIME,
  gcTime: CASES_GC_TIME,
};

export const CASES_QUERY_KEY = 'cases';
export const CASE_QUERY_KEY = 'case';

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  draft: 'Draft',
  open: 'Open',
  in_progress: 'In Progress',
  under_review: 'Under Review',
  resolved: 'Resolved',
  closed: 'Closed',
  archived: 'Archived',
};

export const CASE_STATUS_COLORS: Record<CaseStatus, string> = {
  draft: colors.gray[300],
  open: colors.alternative.blue,
  in_progress: colors.alternative.purple,
  under_review: colors.alternative.gold,
  resolved: colors.alternative.green,
  closed: colors.gray[400],
  archived: colors.gray[500],
};

export const CASE_PRIORITY_LABELS: Record<CasePriority, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export const CASE_PRIORITY_COLORS: Record<CasePriority, string> = {
  low: colors.alternative.green,
  medium: colors.alternative.gold,
  high: colors.alternative.red,
  critical: colors.alternative.red,
};

export const CASE_TYPE_LABELS: Record<HumanCaseType, string> = {
  incident: 'Incident',
  investigation: 'Investigation',
  project_review: 'Project Review',
  case_study: 'Case Study',
  audit: 'Audit',
  compliance: 'Compliance',
};

export const getCaseTypeLabel = (type: CaseType): string =>
  type === 'job_extraction' ? 'Portal Job' : CASE_TYPE_LABELS[type];

export const CASE_SCOPE_LABELS: Record<CaseScope, string> = {
  active: 'Active',
  all: 'Any status',
  closed: 'Closed',
};

export type CaseListTab = 'my' | 'all';

export const CASE_LIST_TABS: { label: string; value: CaseListTab }[] = [
  { label: 'My Cases', value: 'my' },
  { label: 'All Cases', value: 'all' },
];

export const CASE_SCOPE_TABS: { label: string; value: CaseScope }[] = [
  { label: CASE_SCOPE_LABELS.active, value: 'active' },
  { label: CASE_SCOPE_LABELS.all, value: 'all' },
  { label: CASE_SCOPE_LABELS.closed, value: 'closed' },
];

export type CaseSortMode =
  | 'recently_active'
  | 'newest_created'
  | 'oldest_created';

export const DEFAULT_CASE_SORT_MODE: CaseSortMode = 'recently_active';

export const CASE_SORT_MODES: {
  value: CaseSortMode;
  label: string;
  sort?: CaseSortField;
  order?: CaseSortOrder;
}[] = [
  { value: 'recently_active', label: 'Recently active' },
  {
    value: 'newest_created',
    label: 'Newest created',
    sort: 'created_at',
    order: 'desc',
  },
  {
    value: 'oldest_created',
    label: 'Oldest created',
    sort: 'created_at',
    order: 'asc',
  },
];

export const CASE_CLOCK_LABELS = {
  created_at: 'Created',
  last_activity_at: 'Last activity',
  updated_at: 'Updated',
} as const;

/** Allowed next statuses from the cases/validate_transition map. */
export const CASE_TRANSITIONS: Record<CaseStatus, CaseStatus[]> = {
  draft: ['open', 'archived'],
  open: ['in_progress', 'archived'],
  in_progress: ['under_review', 'resolved', 'archived'],
  under_review: ['resolved', 'archived'],
  resolved: ['closed', 'in_progress', 'archived'],
  closed: ['in_progress', 'archived'],
  archived: [],
};

export const CASE_ACTIVITY_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  status_changed: 'Status changed',
  participant_added: 'Participant added',
  participant_removed: 'Participant removed',
  item_added: 'Item linked',
  item_removed: 'Item removed',
  comment: 'Comment',
  resolved: 'Resolved',
  reopened: 'Reopened',
  closed: 'Closed',
};

export const CASE_PARTICIPANT_ROLE_LABELS: Record<string, string> = {
  owner: 'Owner',
  investigator: 'Investigator',
  reviewer: 'Reviewer',
  observer: 'Observer',
  contributor: 'Contributor',
};
